<template>
  <!--
    AB#22138 — a one-line invitation to turn on notifications, for someone who
    may have them (receive-push-notifications) but has not turned them on on
    this device. The browser's own permission prompt only ever comes from the
    "Turn on" tap, never by itself: a cold prompt is the one browsers learn to
    hide, and on an iPhone it is not allowed at all.

    On an iPhone outside the Home Screen app it says how to install instead.
    "Not now" asks again in 3 days; after the third time, in 30. Blocked in
    the phone's settings, or already on: it never shows.
  -->
  <Transition name="pp">
    <aside
      v-if="visible"
      class="pp"
      role="dialog"
      aria-labelledby="pp-title"
      data-test="push-prompt"
    >
      <span class="pp-ic" aria-hidden="true"><BellAlertIcon class="pp-ic-svg" /></span>
      <div class="pp-main">
        <template v-if="install">
          <h2 id="pp-title" class="pp-title">Get notifications on this iPhone</h2>
          <p class="pp-text">
            Add the portal to your Home Screen: tap <b>Share</b>, then <b>Add to Home Screen</b>.
            Open it from there and turn notifications on.
          </p>
          <div class="pp-actions">
            <button
              type="button"
              class="dash-btn dash-btn--primary"
              data-test="push-prompt-ok"
              @click="later"
            >
              Got it
            </button>
          </div>
        </template>
        <template v-else-if="whatsNew">
          <h2 id="pp-title" class="pp-title">New: your orders on your phone</h2>
          <p class="pp-text">
            Orders assigned to you and delayed orders now reach your phone too. Choose which in
            Notifications.
          </p>
          <div class="pp-actions">
            <button
              type="button"
              class="dash-btn dash-btn--primary"
              data-test="push-whatsnew-choose"
              @click="choose"
            >
              Choose
            </button>
            <button
              type="button"
              class="dash-btn"
              data-test="push-whatsnew-ok"
              @click="seenWhatsNew"
            >
              Got it
            </button>
          </div>
        </template>
        <template v-else>
          <h2 id="pp-title" class="pp-title">Get notified when someone needs you</h2>
          <p class="pp-text">
            When an order is assigned to you, an order is delayed, or someone mentions you, straight
            to this device.
          </p>
          <div class="pp-actions">
            <button
              type="button"
              class="dash-btn dash-btn--primary"
              data-test="push-prompt-on"
              :disabled="push.busy.value"
              @click="turnOn"
            >
              Turn on
            </button>
            <button type="button" class="dash-btn" data-test="push-prompt-later" @click="later">
              Not now
            </button>
          </div>
        </template>
      </div>
      <button
        type="button"
        class="dash-icon-btn pp-x"
        :aria-label="whatsNew ? 'Got it' : 'Not now'"
        @click="whatsNew ? seenWhatsNew() : later()"
      >
        <XMarkIcon class="size-5" />
      </button>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { BellAlertIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { somethingIsOpen } from '@/composables/useDeskMessages';
import { showToast } from '@/composables/swal';
import { promptDue, rememberLater, rememberWhatsNew, whatsNewDue } from '@/utils/pushPrompt';

const props = withDefaults(defineProps<{ delayMs?: number }>(), { delayMs: 4000 });

const push = usePushNotifications();
const visible = ref(false);
const install = computed(() => push.state.value === 'needs-install');
const askable = computed(
  () => push.offered.value && (push.state.value === 'off' || push.state.value === 'needs-install')
);
/*
 * AB#22138 — someone who already has notifications on hears once that order
 * notifications arrived, and where to choose them. Only when one of the new
 * kinds can reach them.
 */
const NEW_KINDS = ['assigned', 'account_orders', 'delayed'];
const whatsNewOffered = computed(
  () =>
    push.offered.value &&
    push.state.value === 'on' &&
    push.preferences.value.some((p) => NEW_KINDS.includes(p.type))
);
const whatsNew = ref(false);

let timer: ReturnType<typeof setTimeout> | null = null;
const schedule = (): void => {
  if (timer || visible.value) return;
  timer = setTimeout(function tryShow() {
    timer = null;
    const note = whatsNewOffered.value && whatsNewDue(globalThis.localStorage);
    if (!note && (!askable.value || !promptDue(globalThis.localStorage, Date.now()))) return;
    // Someone in the middle of a form or a sheet is not interrupted: try again shortly.
    if (somethingIsOpen()) {
      timer = setTimeout(tryShow, 15000);
      return;
    }
    whatsNew.value = note;
    visible.value = true;
  }, props.delayMs);
};

watch(
  () => askable.value || whatsNewOffered.value,
  (ok) => (ok ? schedule() : (visible.value = false)),
  { immediate: true }
);

const seenWhatsNew = (): void => {
  rememberWhatsNew(globalThis.localStorage, Date.now());
  visible.value = false;
};
const choose = (): void => {
  seenWhatsNew();
  push.openSettings();
};
onBeforeUnmount(() => timer && clearTimeout(timer));

const later = (): void => {
  rememberLater(globalThis.localStorage, Date.now());
  visible.value = false;
};

const turnOn = async (): Promise<void> => {
  const ok = await push.enable();
  visible.value = false;
  if (ok) {
    showToast('Notifications are on. A test is on its way.', 'success');
    void push.test();
    return;
  }
  if ((push.state.value as string) === 'blocked') {
    showToast(
      "Notifications were not allowed. You can allow them in the phone's settings.",
      'warning'
    );
  } else {
    // Not a refusal: ask again another day rather than straight away.
    rememberLater(globalThis.localStorage, Date.now());
    showToast('Notifications could not be turned on on this device.', 'error');
  }
};
</script>

<style lang="scss">
/* Bottom-right on a desktop, just left of the Ask Atlas launcher; above the
   bottom bar and the launcher on a phone. Under drawers (10035): it never
   covers one. */
.pp {
  position: fixed;
  right: 92px;
  bottom: 20px;
  z-index: 10034;
  width: min(380px, calc(100vw - 32px));
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 10px 14px 14px;
  border-radius: 14px;
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-2, 0 16px 40px rgba(15, 18, 24, 0.22));
}
.pp-ic {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);
}
.pp-ic-svg {
  width: 20px;
  height: 20px;
}
.pp-main {
  flex: 1 1 auto;
  min-width: 0;
}
.pp-title {
  margin: 2px 0 4px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}
.pp-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ink-soft);
}
.pp-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.pp-x {
  flex-shrink: 0;
  margin-top: -2px;
}
@media (max-width: 767.98px) {
  .pp {
    left: 12px;
    right: 12px;
    width: auto;
    bottom: calc(60px + env(safe-area-inset-bottom, 0px) + 76px);
  }
}
.pp-enter-active,
.pp-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.pp-enter-from,
.pp-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
@media (prefers-reduced-motion: reduce) {
  .pp-enter-active,
  .pp-leave-active {
    transition: none;
  }
}
</style>
